import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { wpOffersAPI } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Search, Flame, Loader2, FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const STATUS_BADGES = {
  erstellt: { variant: "secondary", label: "Erstellt" },
  versendet: { variant: "default", label: "Versendet" },
  angenommen: { variant: "default", label: "Angenommen", cls: "bg-green-600" },
  abgelehnt: { variant: "destructive", label: "Abgelehnt" },
};

export default function WPOffersListPage() {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await wpOffersAPI.list();
      setOffers(res.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden der WP-Angebote");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return offers;
    const q = search.toLowerCase();
    return offers.filter(
      (o) =>
        (o.offer_number || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.title || "").toLowerCase().includes(q)
    );
  }, [offers, search]);

  return (
    <div className="space-y-6" data-testid="wp-offers-list-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Flame className="h-7 w-7 text-orange-500" />
            Wärmepumpen-Angebote
          </h1>
          <p className="text-sm text-muted-foreground">
            Eigenständige Angebote für Wärmepumpen-Systeme
          </p>
        </div>
        <Button asChild data-testid="new-wp-offer-btn">
          <Link to="/wp-angebote/neu">
            <Plus className="h-4 w-4 mr-2" /> Neues WP-Angebot
          </Link>
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Nr., Kunde, Titel..."
          className="pl-10"
          data-testid="wp-offers-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" data-testid="wp-offers-loading" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Flame className="h-12 w-12 mx-auto text-orange-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Noch keine WP-Angebote</h2>
            <p className="text-muted-foreground mb-4">
              Starte mit einem strukturierten Wärmepumpen-Angebot in 5 Produktgruppen.
            </p>
            <Button asChild data-testid="empty-state-new-btn">
              <Link to="/wp-angebote/neu">
                <Plus className="h-4 w-4 mr-2" /> Erstes WP-Angebot erstellen
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nr.</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => {
                  const badge = STATUS_BADGES[o.status] || { variant: "secondary", label: o.status };
                  return (
                    <TableRow key={o.id} data-testid={`wp-offer-row-${o.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {o.offer_number}
                          <IncognitoBadge active={o.is_incognito} />
                        </div>
                      </TableCell>
                      <TableCell>{o.customer_name}</TableCell>
                      <TableCell className="max-w-xs truncate">{o.title}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {(o.total || 0).toFixed(2)} €
                      </TableCell>
                      <TableCell>
                        <Badge variant={badge.variant} className={badge.cls}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {o.created_at ? new Date(o.created_at).toLocaleDateString("de-DE") : "—"}
                      </TableCell>
                      <TableCell>
                        <Button asChild size="sm" variant="ghost" data-testid={`view-wp-offer-${o.id}`}>
                          <Link to={`/wp-angebote/${o.id}`}>
                            <FileText className="h-4 w-4 mr-1" /> Details
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
